import { useSelect } from "downshift";
import { groupBy } from "lodash-es";
import type { ReactNode, Ref } from "react";
import { Fragment, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { twJoin } from "tailwind-merge";

import checkIcon from "@/assets/icons/check.svg";
import chevronDownIcon from "@/assets/icons/chevron-down.svg";
import xCloseIcon from "@/assets/icons/x-close.svg";
import { twResolve } from "@/helpers/tw-resolve";
import { useSelectFloating } from "@/ui-adapters/useSelectFloating";

import { fieldControlStateVariants } from "./field-control";

const SELECT_WIDTH_CLASSES = {
  auto: undefined,
  filter: "min-w-select-filter",
  compact: "max-w-select-compact",
  dialog: "mx-auto w-full min-w-select-dialog",
  standard: "max-w-select-standard",
  wide: "max-w-select-wide",
  responsiveFilter: "w-full @sm:w-select-responsive-filter",
} as const;

export type SelectWidth = keyof typeof SELECT_WIDTH_CLASSES;

/**
 * Props for a typed single-value select. `selected` follows the generic item contract, `emptyItem` controls whether
 * clearing is represented, and `width` selects one of the system-owned control measures.
 */
interface SelectBaseProps<
  T,
  TSelected extends T | undefined = T | undefined,
  TEmptyItem extends React.ReactNode | undefined = React.ReactNode | undefined,
> {
  id?: string;
  width?: SelectWidth;
  placeholder?: ReactNode;
  selected: TSelected;
  items: readonly T[];
  renderOption: (item: T, selected: boolean) => ReactNode;
  renderSelected?: (item: T) => React.ReactNode;
  keySelector: (item: T) => string | number;
  groupSelector?: (item: T) => string;
  emptyItem?: TEmptyItem;
  disabled?: boolean;
  onChange?: (item: TEmptyItem extends undefined | null ? T : T | undefined) => void;
  "data-testid"?: string;
  "aria-invalid"?: boolean;
  onBlur?: () => void;
  hideChevron?: boolean;
  maxMenuHeight?: number;
  placement?: "top-left" | "bottom-left" | "top-right" | "bottom-right";
  autoFocus?: boolean;
  ref?: Ref<HTMLButtonElement>;
  usePortal?: boolean;
}

type SelectClearability =
  | {
      isClearable: true;
      clearLabel: string;
    }
  | {
      isClearable?: false;
      clearLabel?: never;
    };

export type SelectProps<
  T,
  TSelected extends T | undefined = T | undefined,
  TEmptyItem extends React.ReactNode | undefined = React.ReactNode | undefined,
> = SelectBaseProps<T, TSelected, TEmptyItem> & SelectClearability;
/** Renders a typed single-value select with an optional empty selection. */
export function Select<
  T,
  TSelected extends T | undefined = T | undefined,
  TEmptyItem extends React.ReactNode | undefined = undefined,
>({
  selected,
  width = "auto",
  disabled,
  placeholder,
  items,
  renderOption,
  renderSelected = (x) => renderOption(x, false),
  keySelector,
  groupSelector,
  onChange,
  emptyItem,
  "aria-invalid": isInvalid,
  onBlur,
  hideChevron,
  id,
  ref,
  autoFocus,
  isClearable,
  clearLabel,
  placement = "bottom-left",
  maxMenuHeight,
  usePortal = false,
  ...props
}: SelectProps<T, TSelected, TEmptyItem>): React.ReactNode {
  const [isClearSelectionHovered, setIsClearSelectionHovered] = useState(false);
  const [showHighlight, setShowHighlight] = useState(false);

  const orderedGroups = useMemo(() => {
    const itemsWithIndex = items.map((item, index) => ({
      item,
      index,
    }));
    if (groupSelector) {
      return Object.entries(groupBy(itemsWithIndex, (entry) => groupSelector(entry.item)));
    }

    return [["", itemsWithIndex] as const];
  }, [items, groupSelector]);

  const { isOpen, getToggleButtonProps, getMenuProps, getItemProps, highlightedIndex } = useSelect<T | null>({
    items: [...(emptyItem == null ? [] : [null]), ...items],
    selectedItem: selected ?? null,
    onSelectedItemChange(changes) {
      if (emptyItem == null && !changes.selectedItem) {
        return;
      }
      onChange?.((changes.selectedItem ?? undefined) as T);
    },
    onHighlightedIndexChange() {
      setShowHighlight(true);
    },
    onIsOpenChange(x) {
      if (x.isOpen) {
        setShowHighlight(false);
      } else {
        onBlur?.();
      }
    },
    defaultIsOpen: autoFocus,
  });
  const handleClear = () => {
    onChange?.(undefined as T);
  };

  const { referenceRef, menuRef, menuProps, opensAbove } = useSelectFloating({
    open: isOpen,
    usePortal,
    placement,
    maxHeight: maxMenuHeight,
  });

  const menuContent = isOpen && (
    <>
      {emptyItem != null && (
        <div
          key="__empty"
          {...getItemProps({
            index: 0,
            item: null,
          })}
          onMouseEnter={() => setIsClearSelectionHovered(true)}
          aria-checked={selected == null}
          className={twJoin(
            "w-full cursor-pointer px-select-option-inline py-select-option-block text-left text-text-primary outline-hidden! select-none first:mt-select-menu-edge last:mb-select-menu-edge",
            isOpen && showHighlight && isClearSelectionHovered && highlightedIndex === 0 && "bg-background-brand-light",
          )}
          onMouseLeave={() => setIsClearSelectionHovered(false)}
        >
          <div
            className={twJoin(
              "flex items-center justify-between gap-select-content",
              selected == null && "text-body-emphasis",
            )}
          >
            {emptyItem || "-"}
            {selected == null ? <SelectIcon name={checkIcon} /> : null}
          </div>
        </div>
      )}
      {orderedGroups.map(([groupTitle, groupItems]) => (
        <Fragment key={groupTitle}>
          {groupTitle && orderedGroups.length > 1 ? (
            <span className="px-select-group-inline pt-select-group-start pb-select-group-end text-caption-emphasis">
              {groupTitle}
            </span>
          ) : null}
          {groupItems.map(({ item, index }) => {
            if (emptyItem != null) {
              index++;
            }
            const key = keySelector(item);
            const isChecked = selected ? keySelector(selected) === key : false;
            const { key: groupItemKey, ...groupItemProps } = getItemProps({
              key,
              index,
              item,
            });

            return (
              <div
                key={groupItemKey}
                {...groupItemProps}
                aria-checked={isChecked}
                className={twJoin(
                  "w-full cursor-pointer px-select-option-inline py-select-option-block text-text-primary outline-hidden! select-none first:mt-select-menu-edge last:mb-select-menu-edge",
                  isOpen &&
                    showHighlight &&
                    !isClearSelectionHovered &&
                    highlightedIndex === index &&
                    "bg-background-brand-light",
                )}
                data-testid="select-item"
                data-value={key}
              >
                <div
                  className={twJoin(
                    "flex items-center justify-between gap-select-content",
                    isChecked && "text-body-emphasis",
                  )}
                >
                  {renderOption(item, isChecked)}
                  {isChecked ? <SelectIcon name={checkIcon} /> : null}
                </div>
              </div>
            );
          })}
        </Fragment>
      ))}
    </>
  );

  const menu = (
    <div
      {...getMenuProps({ ref: menuRef }, { suppressRefError: !isOpen })}
      {...menuProps}
      data-portal-dropdown={usePortal || undefined}
      className={twJoin(
        "z-select-menu flex w-max cursor-default flex-col items-start overflow-auto rounded-lg border bg-background-default outline-hidden! transition-colors",
        opensAbove ? "rounded-b-none" : "rounded-t-none",
        usePortal
          ? "border-border-secondary shadow-overlay hocus:border-border-brand-secondary"
          : "border-border-brand-secondary",
        !usePortal && (opensAbove ? "shadow-select-menu-top" : "shadow-raised"),
      )}
    >
      {menuContent}
    </div>
  );

  return (
    <div
      data-cy-is-selected={!!selected}
      data-testid={props["data-testid"] || "select"}
      className={twJoin(
        "group relative",
        isOpen && "z-20",
        disabled && "pointer-events-none",
        SELECT_WIDTH_CLASSES[width],
      )}
    >
      <div>
        <button
          type="button"
          // oxlint-disable-next-line react-hooks/refs
          {...getToggleButtonProps({
            ref: (node: HTMLButtonElement | null) => {
              referenceRef(node);
              if (typeof ref === "function") {
                ref(node);
              } else if (ref) {
                ref.current = node;
              }
            },
          })}
          disabled={disabled}
          aria-invalid={isInvalid}
          className={twResolve(
            "relative h-control-md w-full cursor-default rounded-lg border border-border-secondary bg-background-default py-select-trigger-block pl-select-trigger-inline text-left leading-select-trigger focus:outline-hidden disabled:bg-background-disabled",
            fieldControlStateVariants({ invalid: isInvalid }),
            isClearable && selected && "pr-select-trigger-clear",
            !(isClearable && selected) && "pr-select-trigger-adornment",
            isOpen && "border-border-brand-secondary",
            isOpen && opensAbove && "rounded-t-none",
            isOpen && !opensAbove && "rounded-b-none",
          )}
          data-testid="select-button"
          id={id}
        >
          <div
            className={twJoin(
              "flex items-center justify-between truncate",
              disabled && "text-text-on-disabled",
              !selected && "text-text-secondary",
            )}
            data-testid="select-placeholder"
            data-value={selected ? keySelector(selected) : undefined}
          >
            {selected ? renderSelected(selected) : placeholder || emptyItem}
          </div>
        </button>
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center gap-select-content pr-select-adornment-inline">
          {isClearable && selected && (
            <button
              aria-label={clearLabel}
              type="button"
              onClick={handleClear}
              className="pointer-events-auto text-text-secondary transition-colors hover:text-text-secondary"
            >
              <SelectIcon name={xCloseIcon} />
            </button>
          )}
          <span
            className={twResolve(
              "pointer-events-none flex items-center text-text-secondary transition-all group-hover:text-text-secondary",
              hideChevron && "opacity-100 @sm:opacity-0 group-hocus:opacity-100",
              isOpen && "@sm:opacity-100",
              disabled && "text-text-on-disabled",
              isInvalid && "text-text-danger",
            )}
          >
            <SelectIcon name={chevronDownIcon} animateRotation rotate={isOpen} />
          </span>
        </div>
      </div>

      {isOpen && (usePortal ? createPortal(menu, document.body) : menu)}
    </div>
  );
}

function SelectIcon({
  name,
  animateRotation = false,
  rotate = false,
  tone = "default",
}: {
  name: string;
  animateRotation?: boolean;
  rotate?: boolean;
  tone?: "default" | "danger";
}): React.ReactNode {
  return (
    <span
      className={twJoin(
        "shrink-0 fill-transparent",
        animateRotation && "transition-transform",
        rotate && "rotate-180",
        tone === "danger" && "text-text-danger-secondary",
      )}
    >
      <svg aria-hidden="true" width={16} height={16} data-testid={name}>
        <use xlinkHref={`#${name}`} />
      </svg>
    </span>
  );
}
